import { createFileRoute, Link } from "@tanstack/react-router";
import { Shield, Lock, Database, Cookie, Users, Mail, Trash2, FileCheck, AlertCircle } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import pageCover from "@/assets/kupish/p-zaupanje.webp";

const TRUST_FAQ: Array<[string, string]> = [
  [
    "Kdo je upravljavec osebnih podatkov na kupish.si?",
    "Upravljavec je ARTEPIN, spletne rešitve, d.o.o., Šišenska cesta 17, 1000 Ljubljana, Slovenija. Vprašanja o zasebnosti sprejemamo na info@kupish.si.",
  ],
  [
    "Ali je nakup na kupish.si varen?",
    "Da. Promet med brskalnikom in strežnikom je zaščiten s TLS-šifriranjem (HTTPS), gesla hranimo samo v obliki varne kriptografske razpršitve, po registraciji pa zahtevamo potrditev e-naslova.",
  ],
  [
    "Katere podatke zbirate?",
    "Samo tiste, ki so potrebni za delovanje storitve: podatke računa kupca ali ponudnika, podatke o naročilih in računih, prijavo na e-novice ter osnovne tehnične dnevnike obiskov.",
  ],
  [
    "Kako uveljavljam svoje pravice po GDPR?",
    "Zahtevo za dostop, popravek, izbris ali prenos podatkov pošljite na info@kupish.si. Zavezujoča pravna besedila so Pravila zasebnosti in Splošni pogoji poslovanja.",
  ],
];

export const Route = createFileRoute("/zaupanje-in-varnost")({
  head: () => {
    const url = "https://kupish.si/zaupanje-in-varnost";
    const title = "Zaupanje in varnost — kupish.si";
    const description =
      "Kako varujemo vaše osebne podatke, kdo jih obdeluje, koliko časa jih hranimo in kako uveljavljate svoje pravice po GDPR.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            inLanguage: "sl-SI",
            mainEntity: TRUST_FAQ.map(([q, a]) => ({
              "@type": "Question",
              name: q,
              acceptedAnswer: { "@type": "Answer", text: a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: title,
            url,
            inLanguage: "sl-SI",
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", "[data-answer]"],
            },
          }),
        },
      ],
    };
  },
  component: Page,
});

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Shield;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="border-t border-border pt-10 mt-10 first:border-0 first:pt-0 first:mt-0">
      <div className="flex items-center gap-3 mb-4">
        <span className="icon-badge-sm">
          <Icon className="h-4 w-4" />
        </span>
        <h2 className="font-serif text-2xl md:text-3xl tracking-tight m-0">{title}</h2>
      </div>
      <div className="article-prose">{children}</div>
    </section>
  );
}

function Page() {
  return (
    <article className="pb-24">
      <PageHero src={pageCover} alt="Zaupanje in varnost na kupish.si — minimalističen 3D prikaz ščita in paketa">
        <Link
          to="/zasebnost"
          className="text-xs uppercase tracking-[0.18em] text-accent hover:underline"
        >
          ← Pravila zasebnosti
        </Link>
        <p className="eyebrow mt-6">Zaupanje in varnost</p>
        <h1 className="font-serif text-4xl md:text-6xl mt-3 leading-[1.05] tracking-tight">
          Vaši podatki, <span className=" text-accent">jasno in pošteno</span>
        </h1>
        <p data-answer className="mt-5 text-base md:text-lg max-w-2xl leading-relaxed">
          <strong>Na kratko:</strong> nakup na kupish.si je varen — povezava je
          zaščitena s HTTPS, gesla hranimo samo razpršena, zbiramo pa zgolj podatke, ki so
          nujni za nakup in dostavo. Upravljavec podatkov je ARTEPIN, spletne rešitve,
          d.o.o. iz Ljubljane; pravice po GDPR uveljavljate na info@kupish.si.
        </p>
        <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl">
          To stran ureja ARTEPIN, spletne rešitve, d.o.o. (kupish.si), da na enem
          mestu odgovori na pogosta vprašanja o varnosti in zasebnosti pri uporabi naše
          platforme. Stran je informativne narave in ni samostojna potrditev skladnosti —
          zavezujoča pravna besedila so{" "}
          <Link to="/zasebnost" className="text-accent underline underline-offset-2">
            Pravila zasebnosti
          </Link>{" "}
          in{" "}
          <Link to="/pogoji" className="text-accent underline underline-offset-2">
            Splošni pogoji poslovanja
          </Link>
          .
        </p>
        <time className="mt-6 block text-sm text-muted-foreground">
          Zadnja posodobitev: junij 2026
        </time>
      </PageHero>

      <div className="mx-auto max-w-3xl px-6 mt-14 md:mt-20">
        <Section icon={Shield} title="Upravljavec podatkov">
          <p>
            Upravljavec osebnih podatkov je <strong>ARTEPIN, spletne rešitve, d.o.o.</strong>,
            Šišenska cesta 17, 1000 Ljubljana, Slovenija. Za vsa vprašanja, povezana z
            zasebnostjo, varnostjo in uveljavljanjem pravic po GDPR, nas kontaktirajte na{" "}
            <a href="mailto:info@kupish.si" className="text-accent underline underline-offset-2">
              info@kupish.si
            </a>
            .
          </p>
        </Section>

        <Section icon={Lock} title="Dostop, prijava in zaščita računa">
          <ul>
            <li>
              Prijava poteka prek e-naslova in gesla. Gesla nikoli ne hranimo v berljivi
              obliki — v podatkovni bazi je shranjena samo varna kriptografska razpršitev.
            </li>
            <li>
              Pri ponastavitvi gesla pošljemo enkratno povezavo na vaš e-naslov; povezava
              je veljavna omejen čas in se po uporabi razveljavi.
            </li>
            <li>
              Po registraciji zahtevamo potrditev e-naslova, da preprečimo zlorabo tujih
              naslovov.
            </li>
            <li>
              Promet med vašim brskalnikom in našimi strežniki je zaščiten s TLS-šifriranjem
              (HTTPS).
            </li>
          </ul>
        </Section>

        <Section icon={Database} title="Kateri podatki se zbirajo">
          <p>Na kupish.si obdelujemo zgolj podatke, ki so potrebni za delovanje storitve:</p>
          <ul>
            <li>
              <strong>Račun kupca:</strong> e-naslov, ime, geslo (razpršeno), naslov za dostavo
              in zgodovina naročil.
            </li>
            <li>
              <strong>Račun ponudnika/prodajalca:</strong> podatki podjetja oz. s.p., kontaktni
              naslov in podatki za izplačila.
            </li>
            <li>
              <strong>Naročila in računi:</strong> kupljeni izdelki, znesek, naslov za dostavo
              in podatki o plačilu.
            </li>
            <li>
              <strong>E-novice:</strong> e-naslov in status prijave (potrjen / odjavljen).
            </li>
            <li>
              <strong>Tehnični dnevniki:</strong> osnovne metapodatke o obiskih (pot, čas,
              splošni podatki o napravi) za delovanje, varnost in odpravljanje napak.
            </li>
          </ul>
        </Section>

        <Section icon={Users} title="Kdo še obdeluje vaše podatke (podizvajalci)">
          <p>
            Za delovanje platforme uporabljamo nekaj zaupanja vrednih ponudnikov. Vsak od
            njih obdeluje le tiste podatke, ki so nujni za njegovo storitev:
          </p>
          <ul>
            <li>
              <strong>Lovable</strong> — gostovanje aplikacije, podatkovna baza in shramba
              datotek (slike izdelkov, profili).
            </li>
            <li>
              <strong>Stripe</strong> — varno procesiranje plačil. Številke kartic gredo
              neposredno k Stripe-u in nikoli ne dosežejo naših strežnikov.
            </li>
            <li>
              <strong>Brevo</strong> — pošiljanje transakcijskih e-sporočil (potrditve naročil,
              ponastavitev gesla) in e-novic.
            </li>
            <li>
              <strong>Google Analytics</strong> — anonimizirana statistika obiska, da bolje
              razumemo, kako se stran uporablja. Analitiko aktiviramo samo, če v{" "}
              <Link to="/piskotki" className="text-accent underline underline-offset-2">
                pasici o piškotkih
              </Link>{" "}
              soglašate.
            </li>
          </ul>
          <p>
            Vaših podatkov ne prodajamo. Ne uporabljamo jih za samodejno odločanje ali
            profiliranje, ki bi imelo pravne učinke.
          </p>
        </Section>

        <Section icon={Cookie} title="Piškotki in sledenje">
          <p>
            Brez vašega soglasja uporabljamo le piškotke, ki so nujni za delovanje strani
            (prijava, košarica, varnostni žetoni). Analitiko in marketinške piškotke
            naložimo samo, če jih izrecno odobrite v pasici. Soglasje lahko kadar koli
            spremenite — podrobnosti najdete na strani{" "}
            <Link to="/piskotki" className="text-accent underline underline-offset-2">
              Piškotki
            </Link>
            .
          </p>
        </Section>

        <Section icon={Trash2} title="Kako dolgo hranimo podatke">
          <ul>
            <li>
              <strong>Račun in profil:</strong> dokler imate pri nas odprt račun. Po izbrisu
              računa podatke odstranimo v razumnem roku, razen tistih, ki jih zakon zahteva,
              da jih zadržimo dlje.
            </li>
            <li>
              <strong>Naročila in računi:</strong> 10 let, kot to zahteva slovenska davčna
              in računovodska zakonodaja (ZDavP-2, ZGD-1).
            </li>
            <li>
              <strong>Prijava na e-novice:</strong> dokler se ne odjavite. Odjava je možna
              z enim klikom v vsakem e-sporočilu ali na strani{" "}
              <Link to="/unsubscribe" className="text-accent underline underline-offset-2">
                Odjava
              </Link>
              .
            </li>
            <li>
              <strong>Tehnični dnevniki in varnostni zapisi:</strong> največ 12 mesecev,
              nato se anonimizirajo ali izbrišejo.
            </li>
            <li>
              <strong>Reševanje reklamacij in sporov:</strong> do izteka zakonske
              odgovornosti za stvarne napake oz. zastaralnih rokov.
            </li>
          </ul>
        </Section>

        <Section icon={FileCheck} title="Vaše pravice po GDPR">
          <p>Kot uporabnik imate naslednje pravice:</p>
          <ul>
            <li>
              <strong>Pravica do dostopa</strong> — kopija osebnih podatkov, ki jih hranimo
              o vas.
            </li>
            <li>
              <strong>Pravica do popravka</strong> — netočne podatke lahko popravite tudi
              sami v svojem{" "}
              <Link to="/racun" className="text-accent underline underline-offset-2">
                računu
              </Link>
              .
            </li>
            <li>
              <strong>Pravica do izbrisa</strong> („pravica do pozabe"), kjer je to skladno
              z zakonskimi obveznostmi.
            </li>
            <li>
              <strong>Pravica do omejitve obdelave</strong> in <strong>do prenosljivosti</strong>{" "}
              podatkov v strojno berljivi obliki.
            </li>
            <li>
              <strong>Pravica do ugovora</strong> obdelavi za neposredno trženje — odjava
              je takojšnja.
            </li>
            <li>
              <strong>Pravica do pritožbe</strong> pri nadzornem organu — Informacijskem
              pooblaščencu RS (
              <a
                href="https://www.ip-rs.si/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline underline-offset-2"
              >
                ip-rs.si
              </a>
              ).
            </li>
          </ul>
          <p>
            <strong>Postopek zahtevka:</strong> zahtevo pošljite z e-naslova, povezanega z
            vašim računom, na{" "}
            <a
              href="mailto:info@kupish.si"
              className="text-accent underline underline-offset-2"
            >
              info@kupish.si
            </a>
            . Identiteto preverimo prek tega e-naslova; pri kompleksnejših zahtevah lahko
            zaprosimo za dodatno potrditev. Odgovorimo praviloma v 30 dneh.
          </p>
        </Section>

        <Section icon={AlertCircle} title="Varnostni incidenti in prijava ranljivosti">
          <p>
            Če opazite varnostno težavo, ranljivost ali sumite na zlorabo računa, nam to
            čim prej sporočite na{" "}
            <a
              href="mailto:info@kupish.si"
              className="text-accent underline underline-offset-2"
            >
              info@kupish.si
            </a>{" "}
            z opisom in po možnosti koraki za reprodukcijo. Prijave obravnavamo zaupno in
            se odzovemo v najkrajšem možnem času. V primeru kršitve varstva osebnih
            podatkov, ki bi pomenila tveganje za prizadete posameznike, ravnamo skladno s
            72-urnim rokom obveščanja po členu 33 GDPR.
          </p>
        </Section>

        <Section icon={Mail} title="Kontakt za zasebnost">
          <p>
            ARTEPIN, spletne rešitve, d.o.o.
            <br />
            Šišenska cesta 17, 1000 Ljubljana
            <br />
            E-pošta:{" "}
            <a
              href="mailto:info@kupish.si"
              className="text-accent underline underline-offset-2"
            >
              info@kupish.si
            </a>
          </p>
        </Section>
      </div>
    </article>
  );
}
